import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <div className="pt-24 pb-12 text-center">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200">
              <i className="ri-file-unknow-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="font-heading text-2xl text-foreground-900 mb-2">
              文章不存在
            </h1>
            <p className="text-foreground-600 mb-6">该文章可能已被删除或链接失效</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full font-semibold text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
          <Footer />
        </div>
      </>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: '首页', to: '/' },
    { label: '综艺资讯', to: '/' },
    { label: article.title },
  ];

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    image: article.cover,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '极速影院',
      url: 'https://www.jisucinema.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.jisucinema.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, item: { '@id': 'https://www.jisucinema.com/', name: '首页' } },
      { '@type': 'ListItem', position: 2, item: { '@id': 'https://www.jisucinema.com/', name: '综艺资讯' } },
      { '@type': 'ListItem', position: 3, item: { name: article.title } },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <article className="pt-16">
          <div className="w-full px-4 md:px-6 pt-6">
            <div className="max-w-4xl mx-auto">
              <Breadcrumb items={breadcrumbItems} />

              {/* Article Header */}
              <header className="mt-2 mb-8">
                <h1 className="font-heading text-2xl md:text-4xl text-foreground-900 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="bg-accent-100 text-accent-800 px-3 py-1 rounded-full text-xs whitespace-nowrap">
                    {article.category}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-user-line"></i>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-calendar-line"></i>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1 whitespace-nowrap">
                    <i className="ri-eye-line"></i>
                    {article.readCount >= 10000
                      ? `${(article.readCount / 10000).toFixed(1)}万`
                      : article.readCount}{' '}
                    阅读
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-4 flex-wrap">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 bg-background-200 text-foreground-600 rounded-full text-xs whitespace-nowrap"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              </header>

              {/* Cover Image */}
              <figure className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.cover}
                  alt={article.title}
                  title={article.title}
                  className="w-full h-auto object-cover"
                />
              </figure>

              {/* Article Content */}
              <div
                className="prose-content mb-12"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Share */}
              <div className="flex items-center gap-3 py-6 border-t border-background-200">
                <span className="text-sm text-foreground-600 whitespace-nowrap">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微博">
                  <i className="ri-weibo-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="分享到微信">
                  <i className="ri-wechat-line text-foreground-600"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-200 hover:bg-primary-100 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link-m text-foreground-600"></i>
                </button>
              </div>
            </div>
          </div>

          {/* Related Articles */}
          {relatedArticles.length > 0 && (
            <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
              <div className="max-w-4xl mx-auto">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/article/${rel.id}`}
                      className="group p-4 rounded-lg bg-background-50 hover:bg-primary-50 transition-all cursor-pointer"
                    >
                      <div className="h-40 rounded-lg overflow-hidden mb-3">
                        <img
                          src={rel.cover}
                          alt={rel.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                        />
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                        {rel.title}
                      </h3>
                      <div className="flex items-center gap-2 text-xs text-foreground-500">
                        <span>{rel.publishDate}</span>
                        <span>·</span>
                        <span>{rel.readCount >= 10000
                          ? `${(rel.readCount / 10000).toFixed(1)}万`
                          : rel.readCount} 阅读</span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </article>

        <Footer />
      </div>
    </>
  );
}